import { type Deck, FORMAT_LABELS, type Format, FormatSchema } from '@ugin/shared';
import { Layers, Plus, Search } from 'lucide-react';
import { motion } from 'motion/react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { type WidgetComponent, type WidgetMeta } from '../_base/types';
import { WidgetContainer } from '../_base/WidgetContainer';

import { ImportDeckDialog } from './ImportDeckDialog';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useDecks } from '@/hooks/useDecks';
import { formatRelativeTime } from '@/lib/utils';



const meta: WidgetMeta = {
  id: 'deck-list',
  name: 'Decks',
  description: 'Your sleeved-up archetypes, recent first.',
  icon: Layers,
  defaultSize: { w: 6, h: 6 },
  category: 'deck',
};

export interface DeckListWidgetProps {
  compact?: boolean;
}

export const DeckListWidget: WidgetComponent<DeckListWidgetProps> = ({ compact = false }) => {
  const [format, setFormat] = useState<Format | 'all'>('all');
  const [search, setSearch] = useState('');
  const [importOpen, setImportOpen] = useState(false);

  const filterFormat = format === 'all' ? undefined : format;
  const { data, isLoading, error, refetch } = useDecks(filterFormat);

  const decks = useMemo(() => {
    const all = data?.decks ?? [];
    if (!search) return all;
    const q = search.toLowerCase();
    return all.filter((d) => d.name.toLowerCase().includes(q));
  }, [data, search]);

  return (
    <>
      <WidgetContainer
        meta={meta}
        icon={<Layers className="size-4" />}
        actions={
          <Button size="sm" onClick={() => { setImportOpen(true); }} className="gap-1">
            <Plus className="size-3.5" />
            Import
          </Button>
        }
        loading={isLoading}
        error={error ?? undefined}
        onRetry={() => void refetch()}
        empty={!isLoading && decks.length === 0}
        emptyTitle="No decks yet"
        emptyDescription="Paste an Arena export to bring your first build into Ugin's view."
        emptyCta={
          <Button onClick={() => { setImportOpen(true); }} className="gap-1">
            <Plus className="size-3.5" />
            Import from Arena
          </Button>
        }
      >
        {!compact && (
          <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ugin-text-faint" />
              <Input
                value={search}
                onChange={(e) => { setSearch(e.target.value); }}
                placeholder="Search decks…"
                className="pl-9"
                aria-label="Search decks"
              />
            </div>
            <Select value={format} onValueChange={(v) => { setFormat(v as Format | 'all'); }}>
              <SelectTrigger className="w-full sm:w-44" aria-label="Filter by format">
                <SelectValue placeholder="Format" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All formats</SelectItem>
                {FormatSchema.options.map((fmt) => (
                  <SelectItem key={fmt} value={fmt}>
                    {FORMAT_LABELS[fmt]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <ul className="space-y-2">
          {decks.map((deck, idx) => (
            <motion.li
              key={deck.id}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.18, delay: Math.min(idx * 0.02, 0.16) }}
            >
              <DeckRow deck={deck} compact={compact} />
            </motion.li>
          ))}
        </ul>
      </WidgetContainer>

      <ImportDeckDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        onImported={(deck) => {
          toast.success(`"${deck.name}" is on the table.`);
          setImportOpen(false);
        }}
      />
    </>
  );
};

DeckListWidget.meta = meta;

function DeckRow({ deck, compact }: { deck: Deck; compact: boolean }) {
  const count = deck.mainboard.reduce((s, e) => s + e.quantity, 0);
  return (
    <Link
      to={`/decks/${deck.id}`}
      className="group flex items-center justify-between gap-3 rounded-lg border border-ugin-border/40 bg-ugin-surface/40 px-3 py-2.5 transition-all hover:border-ugin-border hover:bg-ugin-surface-elevated"
    >
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium text-ugin-text group-hover:text-ugin-accent">
          {deck.name}
        </p>
        {!compact && (
          <p className="mt-0.5 text-xs text-ugin-text-faint">
            {count} cards · updated {formatRelativeTime(deck.updatedAt)}
          </p>
        )}
      </div>
      <Badge variant="default" className="shrink-0 uppercase">
        {FORMAT_LABELS[deck.format]}
      </Badge>
    </Link>
  );
}
